import { promiseFetch } from '@accedo/promise-fetch';
import { logger } from '@accedo/logger';
import { config } from '##/services/store/store';
import { getDeviceInformation } from '##/utils/device';
import { getAdParameters } from '##/utils/AdParameters';
import { FMSResponse, FMSErrorResponse } from './types';

interface App {
  bundle: string;
  region: string;
  googledai: number;
  coppa: number;
}

interface Device {
  lat: number;
  ip: string;
  ua: string;
}

interface Identifiers {
  ifa: string | null;
  ifatype: string;
  emailhash: string;
  subscriberid: string;
  userid: string;
}

interface FMSRequestParams {
  app: App;
  device: Device;
  identifiers: Identifiers;
}

export const FMS_RESPONSE = 'fms-response';
export const FMS_RESPONSE_TIMESTAMP = 'fms-response-timestamp';
const REQUEST_TIMEOUT_MS = 10000;

const fmsRequestParameters = async (): Promise<FMSRequestParams> => {
  const [deviceInfo, adParams] = await Promise.all([
    getDeviceInformation(),
    getAdParameters(),
  ]);

  const { ipAddress, platform, manufacturer } = deviceInfo;
  const { idtype, is_lat } = adParams;
  const { memberEmailHash, n10adId, memberId } = config.$get().user || {};

  return {
    app: {
      bundle: `Tenplay_${manufacturer}-${platform}`,
      region: 'AU',
      googledai: 1,
      coppa: 0,
    },
    device: {
      lat: Number(is_lat),
      ip: ipAddress.ip,
      ua: navigator.userAgent,
    },
    identifiers: {
      emailhash: memberEmailHash,
      subscriberid: memberId,
      userid: n10adId,
      ifa: null,
      ifatype: idtype,
    },
  };
};

export const getCachedFMSResponse = (): FMSResponse | null => {
  const cachedFMSData = localStorage.getItem(FMS_RESPONSE);
  if (!cachedFMSData) return null;

  const cachedTimestamp = Number(localStorage.getItem(FMS_RESPONSE_TIMESTAMP));
  const parsedData = JSON.parse(cachedFMSData) as FMSResponse;
  const fmsIdTTLMs = parsedData.fms_id_ttl * 1000;

  if (Date.now() - cachedTimestamp < fmsIdTTLMs) {
    return parsedData;
  }

  return null;
};

const cacheFMSResponse = (data: FMSResponse): void => {
  localStorage.setItem(FMS_RESPONSE, JSON.stringify(data));
  localStorage.setItem(FMS_RESPONSE_TIMESTAMP, Date.now().toString());
};

export const fetchFMSResponse = async (
  updateCache = false,
): Promise<FMSResponse | FMSErrorResponse> => {
  const { fmsRequestUrl } = config.$get().api;
  const cachedFMSResponse = getCachedFMSResponse();
  const { memberEmailHash, n10adId, UserId } = config.$get().user || {};
  const fallbackIds = memberEmailHash || n10adId || UserId;

  if (!updateCache && cachedFMSResponse) {
    return cachedFMSResponse;
  }

  const params = await fmsRequestParameters();

  try {
    const { data } = await promiseFetch(fmsRequestUrl, {
      method: 'POST',
      data: JSON.stringify(params),
      timeout: REQUEST_TIMEOUT_MS,
    });

    cacheFMSResponse(data);
    return data;
  } catch (err) {
    logger.error(err);

    return {
      imafw__fw_vcid2: fallbackIds,
    };
  }
};
